import React, { useState } from 'react';
import { useTranslation } from '../LanguageContext';
import { notificationPermission, requestNotificationPermission } from '../utils/tabAlert';

/**
 * Opt-in for OS-level desktop notifications.
 *
 * The tab title and favicon badge need no permission and are always on; this
 * only covers the extra step of a notification popping up over other windows.
 * It lives behind a button because browsers reject a permission prompt that is
 * not tied to a user gesture — and a prompt fired on page load just trains
 * people to click "Block".
 */
export default function NotificationSettings() {
    const { t } = useTranslation();
    const [permission, setPermission] = useState(() => notificationPermission());
    const [busy, setBusy] = useState(false);

    const handleEnable = async () => {
        setBusy(true);
        const result = await requestNotificationPermission();
        setPermission(result);
        setBusy(false);
    };

    return (
        <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary-50 text-primary-600 flex items-center justify-center flex-shrink-0">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
            </div>
            <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-800">{t('tab.enableTitle')}</p>
                <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">{t('tab.enableDesc')}</p>

                {permission === 'granted' && (
                    <p className="text-xs font-semibold text-emerald-600 mt-2">✓ {t('tab.enabled')}</p>
                )}
                {permission === 'denied' && (
                    <p className="text-xs font-medium text-amber-600 mt-2">{t('tab.blocked')}</p>
                )}
                {permission === 'unsupported' && (
                    <p className="text-xs font-medium text-slate-400 mt-2">{t('tab.unsupported')}</p>
                )}
                {permission === 'default' && (
                    <button
                        type="button"
                        onClick={handleEnable}
                        disabled={busy}
                        className="btn-primary mt-2 px-4 py-1.5 text-xs"
                    >
                        {t('tab.enableBtn')}
                    </button>
                )}
            </div>
        </div>
    );
}
